import React, { useEffect, useState } from "react";
import { RoleValidator } from "../../../Components/Common";
import { Upload, Trash2, Download, X, Loader } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { motion, AnimatePresence } from "framer-motion";
import { FiInfo } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { useProjects } from "../Hooks/useProjects";
import { ROLES } from "../../../Constants/Strings";
import { formatDateTime } from "../../../Utils";
import { getFileNameFromUrl, getFileIcon } from "../../../Utils/fileUtils";
import { MESSAGES, TOAST_MESSAGES } from "./constants";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

const FilesTab = () => {
  const { instanceId } = useParams();

  const {
    projectFiles,
    totalProjectFiles,
    fetchProjectFiles,
    uploadProjectFileHandler,
    deleteProjectFileHandler,
    loading
  } = useProjects({ projectId: instanceId });

  const [previewFile, setPreviewFile] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filePage, setFilePage] = useState(1);
  const [uploading, setUploading] = useState(false);
  

  const loadFiles = async (reset = false) => {
    try {
      if (reset) {
        setFilePage(1);
      } else {
        setFilePage((prev) => prev + 1);
      }

      await fetchProjectFiles();
      setHasMore(projectFiles.length < totalProjectFiles);
    } catch (err) {
      toast.error(TOAST_MESSAGES.FILES_FETCH_FAILED);
    }
  };

  useEffect(() => {
    loadFiles(true)
  }, [instanceId]);

  const handleDownload = async (file) => {
    try {
      setDownloadingFileId(file.id);
      const response = await fetch(file.file);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getFileNameFromUrl(file.file);
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extractErrorMessage(err) || TOAST_MESSAGES.DOWNLOAD_EXCEL_FAILED);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDelete = async (fileId) => {
    const confirmed = window.confirm(TOAST_MESSAGES.PROJECT_FILE_DELETE_ALERT);
    if (!confirmed) return;

    try {
      await deleteProjectFileHandler(fileId);
      await loadFiles(true);
    } catch (err) {
      toast.error(TOAST_MESSAGES.FILE_DELETE_FAILED);
    }
  };

  const handleUploadInput = async  (e) => {
    const fl = e.target.files && e.target.files[0];
    try {
      setUploading(true);
      await uploadProjectFileHandler(fl);
      await loadFiles(true);
    } catch (err) {
      toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
    } finally {
      e.target.value = "";
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden px-2">
      <div className="flex justify-between items-center pb-2 mb-2">
        <h3 className="text-lg font-semibold text-primary pb-1">
          Files {totalProjectFiles ? `(${totalProjectFiles})` : ""}
        </h3>
      </div>

      <div
        id="project-files-scroll"
        className="flex-1 overflow-y-auto custom-scrollbar"
      >
        <InfiniteScroll
          dataLength={projectFiles.length}
          next={() => loadFiles(false)}
          hasMore={hasMore}
          loader={
            loading && (
              <div className="flex justify-center items-center h-[100px]">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500"></div>
              </div>
            )
          }
          endMessage={
            projectFiles.length > 0 ? (
              <p className="text-center text-gray-400 text-xs py-2">
                {MESSAGES?.ALL_FILES_LOADED}
              </p>
            ) : null
          }
          scrollableTarget="project-files-scroll"
        >
          <div className="flex flex-col space-y-2">
            {projectFiles.length > 0 ? (
              projectFiles.map((file) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md gap-2 px-3 py-2"
                >
                  <div className="flex items-center gap-2 w-[70%]">
                    <span>{getFileIcon(getFileNameFromUrl(file.file))}</span>

                    <div className="truncate">
                      <p className="text-sm font-medium truncate">
                        {getFileNameFromUrl(file.file)}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {file.created_by} • {formatDateTime(file.created)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <button
                      onClick={() => setPreviewFile(file)}
                      className="p-1.5 rounded-full hover:bg-gray-200"
                    >
                      <FiInfo className="text-primary" size={16} />
                    </button>

                    <button
                      onClick={() => handleDownload(file)}
                      className="p-1.5 rounded-full hover:bg-gray-200"
                    >
                      {downloadingFileId === file.id ? (
                        <Loader className="h-4 w-4 animate-spin text-gray-600" />
                      ) : (
                        <Download className="text-gray-600" size={16} />
                      )}
                    </button>

                    <RoleValidator
                      allowedRoles={[
                        ROLES.GOVERNMENT_OFFICER,
                        ROLES.MANAGING_DIRECTOR,
                        ROLES.HEAD_OFFICE,
                        ROLES.PROJECT_MANAGER,
                        ROLES.PROJECT_ENGINEER,
                      ]}
                      mode="disable"
                    >
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="p-1.5 rounded-full hover:bg-gray-200"
                      >
                        <Trash2 className="text-red-500" size={16} />
                      </button>
                    </RoleValidator>
                  </div>
                </motion.div>
              ))
            ) : !loading ? (
              <p className="text-gray-400 text-sm text-center py-6">
                {MESSAGES?.NO_FILES_AVAILABLE || "No files available"}
              </p>
            ) : (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </InfiniteScroll>
      </div>

      <div className="flex items-center justify-between my-2 pt-2">
        <RoleValidator
          allowedRoles={[
            ROLES.GOVERNMENT_OFFICER,
            ROLES.MANAGING_DIRECTOR,
            ROLES.HEAD_OFFICE,
            ROLES.PROJECT_MANAGER,
            ROLES.PROJECT_ENGINEER,
          ]}
          mode="disable"
        >
          <label className="flex items-center gap-1 text-xs bg-blue-500 text-white rounded-md px-3 py-1 cursor-pointer hover:bg-blue-600 transition">
            <Upload size={14} />
            {uploading ? "Uploading..." : "Upload"}
            <input
              type="file"
              className="hidden"
              onChange={handleUploadInput}
              disabled={uploading}
            />
          </label>
        </RoleValidator>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewFile && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-md p-4 max-w-3xl w-full relative"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
            >
              <button
                onClick={() => setPreviewFile(null)}
                className="absolute top-3 right-3 text-gray-500 hover:text-black"
              >
                <X />
              </button>
              <h3 className="font-semibold mb-3 text-primary">
                {getFileNameFromUrl(previewFile.file)}
              </h3>

              {/\.(jpg|jpeg|png|gif)$/i.test(
                getFileNameFromUrl(previewFile.file)
              ) ? (
                <img
                  src={previewFile.file}
                  alt="preview"
                  className="max-h-[70vh] mx-auto"
                />
              ) : /\.pdf$/i.test(getFileNameFromUrl(previewFile.file)) ? (
                <iframe
                  src={previewFile.file}
                  className="w-full h-[70vh] rounded-md"
                  title="file preview"
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                  {getFileIcon(getFileNameFromUrl(previewFile.file))}
                  <p className="mt-2">Preview not available</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilesTab;
